<template>
  <WorkPackageSubPageModule
    :workpackage="workpackage"
    :isMobile="isMobile"
    :location="location"
    :loading="loading"
    :is_create_screen="is_create"
    :save_btn_loading="save_btn_loading"
    :header_options="[
      {description: 'Download', icon: 'download', show: true},
      {description: 'Delete', icon: 'bin-delete', show: !is_create && isEditMode, delete_btn_loading: delete_btn_loading},
    ]"
    :background_color="workpackage_bg_hex"
    :foreground_color="workpackage_fg_hex"
    @headerBack="navigateBack()"
    @headerCancel="navigateBack()"
    @headerSave="createImage()"
    @clickHeaderDownload="$refs.download_button_test.click()"
    @clickHeaderDelete="deleteImage()"
  >
    <template #header-rightside>
      <!-- quickfix download dummy button-->
      <a
        ref="download_button_test"
        v-show="false"
        download
        :href="image.image"
      />
    </template>
    <template #sort-buttons>
      <template v-if="!is_create">
        <!-- NAVIGATION -->
        <!-- SHOW PREVIOUS -->
        <button
          class="work-package-image-detail-navigation-button"
          v-if="current_index > 0"
          :style="`border-color:${workpackage_fg_hex};`"
          v-ripple
          @click="$router.push({
            name: `${location}WorkPackageImagesDetails`,
            params: {
              image_id: images[current_index - 1].id
              }
          })"
        >
          <TIcon name="arrow-left"/>
        </button>
        <div v-else class="nav-button-placeholder"/>
        <!-- IMAGE NUMBER -->
        <TText
          :text="`${current_index + 1}/${images.length}`"
          :size="14"
          :color="workpackage_fg_hex"
        />

        <!-- SHOW NEXT -->
        <button
          class="work-package-image-detail-navigation-button"
          v-if="current_index < images.length-1"
          :style="`border-color:${workpackage_fg_hex};`"
          v-ripple
          @click="$router.push({
            name: `${location}WorkPackageImagesDetails`,
            params: {
              image_id: images[current_index + 1].id
              }
          })"
        >
          <TIcon name="arrow-right"/>
        </button>
        <div v-else class="nav-button-placeholder"/>
      </template>
    </template>

    <template #body>
      <div v-if="show_zoom" @click="show_zoom=false" class="image-zoom-outside"></div>
      <div v-if="show_zoom" class="image-zoom">
        <v-img
          :max-width="'80vw'"
          :max-height="'80vh'"
          contain
          :src="image.image"
          :key="image.id"
        />
      </div>
      <!-- IMAGE -->
      <div class="work-package-image-detail-img-container">
        <v-btn
          v-if="is_create"
          :class="`work-package-image-detail-img-edit`"
          :style="`background-color: ${workpackage_fg_hex}`"
          icon
          @click="$refs.select_image_button.click()"
        >
          <TIcon
            name="camera-simple"
            :color="workpackage_bg_hex"
          />
        </v-btn>
        <input
          v-show="false"
          ref="select_image_button"
          type="file"
          accept="image/*"
          @change="selectImage($refs.select_image_button.files[0])"
          />
        <transition name="appear">
          <v-img
            class="work-package-image-detail-img"
            max-height="225"
            width="100%"
            contain
            :src="image.image"
            :key="image.id"
          >
            <v-btn
              class="work-package-image-detail-scale-up"
              icon
              @click="show_zoom=true"
            >
              <TIcon
                name="scale-up"
                color="white"
                shadow
              />
            </v-btn>
          </v-img>
        </transition>
      </div>

      <!-- DESCRIPTION -->
      <DisableableInputField
        class="mt-2 mb-2"
        :label="$t('WorkpackageRework.Info.Images.Description')"
        :value="image.comment"
        :is_disabled="!(isEditMode || is_create)"
        :background_color="workpackage_bg_hex"
        :foreground_color="workpackage_fg_hex"
        @change="is_create?
        new_image.comment = $event
        :
        updateUObject({
            url: workpackageimage_url,
            update: {
              comment: $event
            }
          })"
      />

      <!-- LINK -->
      <DropDownOutlinedFlat
        :label="$t('WorkpackageRework.Info.Images.Link')"
        :options="image_link_types"
        :value="{text: image_link.text, value: image_link.value}"
        :is_disabled="!(isEditMode || is_create)"
        :background_color="workpackage_bg_hex"
        :foreground_color="workpackage_fg_hex"
        @change="changeImageLinkType($event)"
        @click.native="(isEditMode || is_create)?'':$router.push({
          name: `${location}WorkPackage${image_link.name_for_route}StepsDetails`,
          params: {
            image_id: image_link.id
          }
          })"
        style="width: 100%;"
      />

      <!-- OPTIONAL WORKSTEP DROPDOWN -->
      <DropDownOutlinedFlat
        v-if="image_link.value !== `${my_prefix}workpackage`"
        :label="$t('WorkpackageRework.Info.Images.Step')"
        :options="image_link_steps"
        :value="{text: $store.state.Ultra[image_link.value][image_link.id].text, value: image_link.id}"
        :is_disabled="!(isEditMode || is_create)"
        :background_color="workpackage_bg_hex"
        :foreground_color="workpackage_fg_hex"
        @change="changeImageLinkStep($event)"
        @click.native="(isEditMode || is_create)?loadSteps():$router.push({
          name: `${location}WorkPackage${image_link.name_for_route}StepsDetails`,
          params: {
            image_id: image_link.id
          }
          })"
        style="width: 100%;"
      />

      <!-- USER -->
      <DisableableInputField
        class="mt-2 mb-2"
        :label="$t('WorkpackageRework.Info.Images.User')"
        :value="image.user"
        :is_disabled="true"
        :background_color="workpackage_bg_hex"
        :foreground_color="workpackage_fg_hex"
      />

      <!-- CREATED -->
      <DisableableInputField
        class="mt-2 mb-2"
        :label="$t('WorkpackageRework.Info.Images.Created')"
        :value="image.created"
        :is_disabled="true"
        :background_color="workpackage_bg_hex"
        :foreground_color="workpackage_fg_hex"
      />

      <!-- AI CLASSIFICATION -->
      <DropDownOutlinedFlat
        :label="$t('WorkpackageRework.Info.Images.AIClassification')"
        :options="ai_classification_options"
        :value="ai_classification_options[0]"
        :background_color="workpackage_bg_hex"
        :foreground_color="workpackage_fg_hex"
        @change="is_create?
        new_image.ai_classification = $event
        :
        updateUObject({
            url: workpackageimage_url,
            update: {
              ai_classification: $event
            }
          })"
        style="width: 100%;"
      />
      <span style="color:black" v-if="!is_create && !backlog">
        <AIIcon
          width="150px"
          color="black"
          :animation="true"
          v-if="confidence_loading"
        />
        <span v-else>
          <b>confidence:</b><br>
          drywall: {{ parseInt($store.state.TWorkPackage.image_confidence.confidence_drywall) }}% <br>
          paint: {{ parseInt($store.state.TWorkPackage.image_confidence.confidence_paint) }}% <br>
          electrical: {{ parseInt($store.state.TWorkPackage.image_confidence.confidence_electrical) }}%
        </span>
      </span>
    </template>
  </WorkPackageSubPageModule>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { WorkPackageReworkMixin } from '@/mixins/WorkPackageRework.mixin.js'

import WorkPackageSubPageModule from '@/modules/WorkPackageWIP/WorkPackageSubPageModule.module.vue';
import TText from '@/components/Taktify/TText.component.vue';
import TIcon from '@/components/Taktify/TIcon.component.vue';
import AIIcon from '@/components/WorkBench/AIIcon.component.vue';
import DisableableInputField from '@/components/WorkPackageWIP/DisableableInputField.component.vue'
import DropDownOutlinedFlat from '@/components/WorkPackageWIP/DropDownOutlinedFlat.component.vue';


  export default {
    components: {
      WorkPackageSubPageModule,
      TText,
      TIcon,
      DisableableInputField,
      DropDownOutlinedFlat,
      AIIcon,
    },

    mixins: [
      WorkPackageReworkMixin
    ],

    props: [
      'location',
      'nav_back',
      'step_type',
    ],

    data: () => ({
      my_prefix: 't',
      confidence_loading: true,
      show_zoom: false,
      ai_classification_options: [
        { text: 'None', value: 'none' },
        { text: 'Drywall', value: 'drywall' },
        { text: 'Electrical', value: 'electrical' },
        { text: 'Paint', value: 'paint' },
        { text: 'Tiles', value: 'tiles' },
      ],
      new_image: {
        comment: '',
        image: '',
        user: '',
        created: '',
        tdesignsteps: [],
        tlogisticsteps: [],
        tmaterialsteps: [],
        troboticsteps: [],
        tworksteps: [],
        tcoststeps: [],
        tqualitysteps: [],
        tdefectsteps: [],
        thandoversteps: [],

        wbdesignsteps: [],
        wblogisticsteps: [],
        wbmaterialsteps: [],
        wbroboticsteps: [],
        wbworksteps: [],
        wbcoststeps: [],
        wbqualitysteps: [],
        wbdefectsteps: [],
        wbhandoversteps: [],
        file: '',
      }
    }),

    created() {
      
      if (this.prefix) {
        this.my_prefix = this.prefix;
      }


      if (!this.is_create && !this.backlog) {
        let self = this;
        this.requestConfidence({
          project_id: this.project_id,
          train_id: this.train_id,
          taktarea_id: this.taktarea_id,
          twaggon_id: this.twaggon_id,
          tworkpackage_id: this.workpackage_id,
          image_id: this.image_id
        }).then(() => {
          self.confidence_loading = false;
        });
      }
    },

    mounted() {
      if (this.is_create) {
        this.new_image.user = `${this.$store.state.Auth.current_user.first_name} ${this.$store.state.Auth.current_user.last_name}`
        this.$refs.select_image_button.click();
      }
    },

    watch: {
      // content_id() {
      image_id() {
        // this.loadData();
        if (this.is_create) {
          this.new_image.user = `${this.$store.state.Auth.current_user.first_name} ${this.$store.state.Auth.current_user.last_name}`
          this.$refs.select_image_button.click();
        }
      },
      workpackage_id() {
        this.$router.push({name: `${this.location}WorkPackageImages`})
      }
    },

    computed: {
      ...mapGetters('Ultra', [
        'getUObjects',
        'getUObject',
      ]),
      ...mapGetters('WorkPackageImage', [
        'getWorkPackageImagesSorted'
      ]),
      workpackageimage_url() {
        return this.workpackage_url.concat([
          {[`${this.my_prefix}workpackageimages`]: this.image.id},
        ])
      },
      image_id() {
        return this.$route.params.image_id;
      },

      images() {
        if(this.$route.params.content_id) {
          // in step
          let img_cat = this.getWorkPackageImagesSorted({workpackage: {[`${this.my_prefix}workpackageimages`]: this.getUObject({ type: `${this.my_prefix}${this.step_type}steps`, id: this.$route.params.content_id})[`${this.my_prefix}workpackageimages`]}, prefix: this.prefix});
          let all_imgs = [].concat.apply([], Object.values(img_cat));
          return all_imgs;
        }
        else {
          // in images
          let img_cat = this.getWorkPackageImagesSorted({workpackage: this.workpackage, prefix: this.prefix});
          let all_imgs = [].concat.apply([], Object.values(img_cat));
          return all_imgs;
        }
      },

      current_index() {
        return this.images.findIndex(img => img.id == this.image_id);
      },

      image() {
        if (!this.is_create) {
          if (this.$store.state.Ultra[`${this.my_prefix}workpackageimages`][this.image_id]) {
            return this.$store.state.Ultra[`${this.my_prefix}workpackageimages`][this.image_id];
          } else {
            return {image: '', id: this.image_id};
          }
        } else {
          if(this.$route.params.content_id) {
            // in step
            let img = JSON.parse(JSON.stringify(this.new_image));
            img[`t${this.step_type}steps`].push(this.$route.params.content_id);
            return img;
            // this.new_image[`t${this.step_type}steps`].push(this.$route.params.content_id);
          }
          return this.new_image;
        }
      },

      image_link_types() {
        let link_types = [{text: 'WORK.package', value: `${this.my_prefix}workpackage`, disabled: false}]
        this.$store.state.Ultra.step_types.forEach(type => {
          if (!['material', 'robotic', 'cost'].includes(type)) {
            let text = `${type.toUpperCase()}.step`;
            let value = `${this.my_prefix}${type}steps`;
            let name_for_route = `${type.substring(0,1).toUpperCase()}${type.substring(1)}`;
            let disabled = true;
            if (this.workpackage[value] && this.workpackage[value].length !== 0) {
              disabled = false;
            } else if (this.workpackage[`${this.my_prefix}${type}_step_count`] && this.workpackage[`${this.my_prefix}${type}_step_count`] !== 0) {
              disabled = false;
            }
            link_types.push({
              text: text,
              value: value,
              disabled: disabled,
              name_for_route: name_for_route,
            })
          }
        });
        return link_types;
      },

      image_link() {
        let link = {value: `${this.my_prefix}workpackage`, id: -1, text: 'WORK.package'}
        this.image_link_types.forEach(link_type => {
          if (this.image[link_type.value] && link_type.value !== `${this.my_prefix}workpackage` && this.image[link_type.value].length !== 0) {
            link.value = link_type.value;
            link.text = link_type.text;
            link.id = this.image[link_type.value][0];
            link.name_for_route = link_type.name_for_route;
          }
        });
        return link;
      },

      image_link_steps() {
        if (this.workpackage[this.image_link.value].length !== 0) {
          return this.workpackage[this.image_link.value].map(id => {
            return {text: this.$store.state.Ultra[this.image_link.value][id].text, value: id, disabled: false};
          });
        } else {
          return [{text: this.$store.state.Ultra[this.image_link.value][this.image_link.id].text, value: this.image_link.id, disabled: false}];
        }
      },
    },

    methods: {
      ...mapActions('Ultra', [
        'updateUObjectRelations'
      ]),
      ...mapActions('TWorkPackage', [
        'requestConfidence'
      ]),

      navigateBack() {
        let name = this.$route.name;
        switch (name) {
          case `${this.location}WorkPackageCamera`:
            this.$router.push({name: `${this.location}WorkPackageInfo`});
            break;
          case `${this.location}WorkPackageImagesDetails`:
            this.$router.push({name: this.nav_back});
            break;
          default:
            // this.$router.push({name: `${this.location}WorkPackageInfo`});
            break;
        }
      },

      changeImageLinkType(evt) {
        let old_step_id = JSON.parse(JSON.stringify(this.image_link.id));
        let old_link_type = JSON.parse(JSON.stringify(this.image_link.value));
        let promises = [];
        if (evt !== `${this.my_prefix}workpackage`) {
          promises.push(this.$store.dispatch('Ultra/requestUObject', {
            url: this.workpackage_url.concat([
              {[evt]: ''},
              {showWithEntitiesNormalized: ''}
            ]),
            add_u_object: true
          }));
        }
        Promise.all(promises).then(() => {
          let promises2 = [];
          if (evt !== `${this.my_prefix}workpackage`) {
            if (this.is_create) {
              this.new_image[evt].push(this.workpackage[evt][0]);
            } else {
              promises2.push(this.updateUObjectRelations({
                url: this.workpackageimage_url,
                target_type: evt,
                target_ids: [this.workpackage[evt][0]],
                action: 'add',
                bidirectional: true
              }));
            }
          }
          Promise.all(promises2).then(() => {
            if (old_link_type !== `${this.my_prefix}workpackage`) {
              if (this.is_create) {
                let index = this.new_image[old_link_type].indexOf(old_step_id);
                this.new_image[old_link_type].splice(index, 1);
              } else {
                this.updateUObjectRelations({
                  url: this.workpackageimage_url,
                  target_type: old_link_type,
                  target_ids: [old_step_id],
                  action: 'remove',
                  bidirectional: true
                })
              }
            }
          });
        });
      },

      changeImageLinkStep(evt) {
        let old_step_id = JSON.parse(JSON.stringify(this.image_link.id));
        if (this.is_create) {
          this.new_image[this.image_link.value].push(evt);
          let index = this.new_image[this.image_link.value].indexOf(old_step_id);
          this.new_image[this.image_link.value].splice(index, 1);
        } else {
        this.updateUObjectRelations({
            url: this.workpackageimage_url,
            target_type: this.image_link.value,
            target_ids: [evt],
            action: 'add',
            bidirectional: true
          }).then(() => {
            this.updateUObjectRelations({
                url: this.workpackageimage_url,
                target_type: this.image_link.value,
                target_ids: [old_step_id],
                action: 'remove',
                bidirectional: true
              })
            })
        }
      },

      selectImage(img) {
        // let image_name = image.name;
        // let arr = image_name.split(".");
        // let ext = arr[arr.length-1];
        //
        // let type = getFileTypeFromExtenion(ext);
        //
        // this.new_image.extension = ext;
        this.new_image.file = img;
        this.new_image.image = URL.createObjectURL(img);
        // this.new_image.name = image.name;
        // this.new_image.type = type;
        // this.update_key_add_image++;
      },

      deleteImage() {
        this.delete_btn_loading = true,
        this.deleteUObject({
          url: this.workpackageimage_url
        }).then(() => {
            this.delete_btn_loading = false,
            this.navigateBack()
          })
      },

      createImage() {
        if (this.new_image.file) {
          this.save_btn_loading = true;
          let image = new FormData();
          image.append("image", this.new_image.file);
          this.createUObject({
            url: this.workpackage_url.concat([
              {[`${this.my_prefix}workpackageimages`]: ''},
              {showWithEntitiesNormalized: ''}
            ]),
            image: image
          }).then(id => {
            this.updateUObject({
              url: this.workpackage_url.concat([
                {[`${this.my_prefix}workpackageimages`]: id}
              ]),
              update: {
                comment: this.new_image.comment
              }
            }).then(() => {
              let promises = [];
              if (this.image_link.value !== `${this.my_prefix}workpackage`) {
                promises.push(this.updateUObjectRelations({
                    url: this.workpackage_url.concat([
                      {[`${this.my_prefix}workpackageimages`]: id}
                    ]),
                    target_type: this.image_link.value,
                    target_ids: [this.image_link.id],
                    action: 'add',
                    bidirectional: true
                  }));
              }
              Promise.all(promises).then(() => {
                this.save_btn_loading = false;
                this.navigateBack();
              })
            });
          });
        }
      },

      loadData() {
        this.loading = true;
        let promises = [];
        // promises.push(this.$store.dispatch('Ultra/requestUObject', {
        //     url: [
        //       {projects: this.project_id},
        //       {trains: this.train_id},
        //       {taktareas: this.taktarea_id},
        //       {twaggons: this.twaggon_id},
        //       {tworkpackages: this.tworkpackage_id},
        //       {tworkpackageimages: this.image_id},
        //       {showWithEntitiesNormalized: ''}
        //     ],
        //   }));
        if (this.image_link.value !== 'tworkpackage') {
          //   promises.push(this.$store.dispatch('Ultra/requestUObject', {
          //   url: [
          //     {projects: this.project_id},
          //     {trains: this.train_id},
          //     {taktareas: this.taktarea_id},
          //     {twaggons: this.twaggon_id},
          //     {tworkpackages: this.tworkpackage_id},
          //     {[this.image_link.value]: ''},
          //     {showWithEntitiesNormalized: ''}
          //   ],
          //   add_u_object: true
          // }));
        }
        Promise.all(promises)
        .then(() => {
          this.loading = false;
        });
      },

      loadSteps() {
        this.$store.dispatch('Ultra/requestUObject', {
        url: this.workpackage_url.concat([
          {[this.image_link.value]: ''},
          {showWithEntitiesNormalized: ''}
        ]),
        add_u_object: true
      });
      }
    },
  }
</script>
<style>
  .work-package-image-detail-navigation-button {
    width: 50px;
    height: 50px;
    border: 1px solid;
    border-radius: 4px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .nav-button-placeholder {
    width: 50px;
    height: 50px;
  }
  .work-package-image-detail-img-container {
    position: relative;
    width: 100%;
    height: 225px;
    border: 1px solid rgba(0,0,0, 0.25);
    border-radius: 4px;
    overflow: hidden;
    flex-shrink: 0;
  }
  .work-package-image-detail-img-edit {
    position: absolute;
    right: 0px;
    z-index: 10;
    margin: 5px;
  }
  .work-package-image-detail-img {
    position: absolute;
    /* background-color: rgba(0, 0, 0, 0.05); */
  }
  .work-package-image-detail-scale-up {
    position: absolute;
    bottom: 5px;
    right: 5px;
  }
  .image-zoom {
    height: 80vh;
    width: 80vw;
    position: fixed;
    background-color: #1E1E1E;
    top: 5vh;
    left: 10vw;
    z-index: 30;
    border-radius: 4px;
  }

  .image-zoom-outside {
    height: 100vh;
    width: 100vw;
    position: fixed;
    top: 0px;
    left: 0px;
    /* background-color: red; */
    z-index: 25;
  }

</style>
